import React, { useEffect, useState } from "react";
import { Warning, Cube, Lightning, StackSimple, Hourglass, CirclesThreePlus, ArrowsLeftRight } from "@phosphor-icons/react";
import { useChainTip, useNetworkStats, useExternalStatus } from "@/hooks";
import { cn, formatBigCount, formatCountdown, formatLovelace, formatRelativeTime } from "@/lib/format";
import { CARDANO_NETWORK } from "@/lib/constants";

// Cardano epoch length (mainnet + preprod both use this since Shelley): 432_000 slots per epoch (5 days).
const PREPROD_EPOCH_LENGTH = 432000;

// Styles + Tile are module-level (NOT defined inside the component) so the
// 1s re-render tick doesn't recreate the component type and remount every
// tile each second — which previously restarted the flash animation and made
// the whole widget flicker.
const tileCls = "relative overflow-hidden rounded-xl border border-primary/20 bg-card/40 backdrop-blur-md px-4 py-3.5 flex items-center gap-3 box-glow transition-colors hover:border-primary/40";
const valueCls = "mt-0.5 font-heading text-[16px] md:text-[18px] leading-tight tabnum text-[#22d3ee] text-glow-cyan whitespace-nowrap";
const labelCls = "text-[10px] uppercase tracking-[0.22em] text-muted-foreground mono whitespace-nowrap";
const hintCls = "mt-1 text-[9px] uppercase tracking-[0.16em] text-muted-foreground mono";
const skeleton = (w) => <span className={cn("inline-block h-5 bg-primary/15 rounded-sm animate-pulse", w)} />;

const Tile = ({ icon: Icon, label, value, hint, testid, flash: cellFlash }) => (
  <div data-testid={testid} className={cn(tileCls, cellFlash && "flash-glow")}>
    {Icon ? <Icon size={24} weight="duotone" className="text-primary shrink-0" /> : null}
    <div className="min-w-0">
      <div className={labelCls}>{label}</div>
      <div className={valueCls}>{value}</div>
      {hint ? <div className={hintCls}>{hint}</div> : null}
    </div>
  </div>
);

// ---------- chain tip widget (6 cells with cyan glow) ----------
export const ChainTipWidget = () => {
  const { tip, error } = useChainTip(5000);
  const { stats } = useNetworkStats(60000);
  const { apiStatus } = useExternalStatus();
  // tick a render every 1s so the slot/countdown stays current between polls
  const [, setNow] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setNow((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // Flash the Tip cell whenever a new block arrives.
  const prevHeightRef = React.useRef(null);
  const [flash, setFlash] = useState(false);
  useEffect(() => {
    if (tip?.height == null) return;
    if (prevHeightRef.current != null && tip.height > prevHeightRef.current) {
      setFlash(true);
      prevHeightRef.current = tip.height;
      const t = setTimeout(() => setFlash(false), 2500);
      return () => clearTimeout(t);
    }
    prevHeightRef.current = tip.height;
  }, [tip?.height]);

  if (error && !tip) {
    return (
      <section data-testid="chain-tip" className="space-y-3">
        <div className="text-[10px] uppercase tracking-[0.25em] text-primary/70 mono">
          chain tip
        </div>
        <div className="border border-destructive/30 bg-destructive/5 px-4 py-3 flex items-center gap-3 text-[11px] uppercase tracking-[0.2em] text-destructive mono rounded-2xl">
          <Warning size={12} />
          chain tip unavailable · {error.message || "API error"}
        </div>
      </section>
    );
  }

  const loading = !tip;

  // The external Cardano data API's own upstream sync can occasionally lag
  // significantly behind real time — when that happens, showing a pulsing
  // "LIVE" dot and a countdown that reads "ended" is misleading rather than
  // informative, so we swap to a neutral "last synced" indicator instead.
  const nowSecForStaleness = Math.floor(Date.now() / 1000);
  const isStale = tip?.time ? nowSecForStaleness - tip.time > 3600 : false;

  // The header dot/label reflects the external API's own reported sync state
  // (from /api/external-status), with four clearly-separated states:
  //   green  SYNCING        — database healthy, tip advancing
  //   amber  SYNC PAUSED    — database healthy, tip stalled
  //   red    SYNC STOPPED   — CONFIRMED database failure (db_connected=false)
  //   grey   STATUS UNKNOWN — our monitoring endpoint/provider was unreachable,
  //                           so we can't confirm anything (not an alarm)
  const netLabel = `${CARDANO_NETWORK === "mainnet" ? "mainnet" : "preprod"} chain tip`;
  const UNKNOWN = { color: "bg-slate-400", label: "status unknown", labelColor: "text-slate-300", pulse: false, detail: "monitor unreachable" };
  const syncDot = {
    online: { color: "bg-emerald-400", label: "syncing", labelColor: "text-[#22d3ee] text-glow-cyan", pulse: true, detail: netLabel },
    sync_stalled: { color: "bg-amber-400", label: "sync paused", labelColor: "text-amber-300", pulse: false, detail: "db healthy · tip stalled" },
    db_down: { color: "bg-red-500", label: "sync stopped", labelColor: "text-red-300", pulse: false, detail: "" },
    unreachable: UNKNOWN,
  }[apiStatus] || UNKNOWN;

  // ---- Live counters derived client-side (1 slot = 1 second on Cardano).
  // Extrapolate the absolute slot from the last seen block time so the UI
  // keeps ticking between polls.
  const nowSec = Math.floor(Date.now() / 1000);
  const elapsed = tip ? Math.max(0, nowSec - (tip.time || nowSec)) : 0;
  const liveAbsoluteSlot = tip ? (tip.slot ?? 0) + elapsed : 0;
  const liveEpochSlot = tip ? Math.min(PREPROD_EPOCH_LENGTH, (tip.epoch_slot ?? 0) + elapsed) : 0;
  // Real epoch end = epoch start unix + epoch length in seconds.
  // Epoch start unix can be derived from the chain tip: (tip.time - tip.epoch_slot)
  const epochStartUnix = tip ? (tip.time || nowSec) - (tip.epoch_slot || 0) : null;
  const epochEndUnix = epochStartUnix != null ? epochStartUnix + PREPROD_EPOCH_LENGTH : null;

  return (
    <section data-testid="chain-tip">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {/* sync status */}
        <div data-testid="tip-cell-sync" className={tileCls}>
          <span className="relative inline-flex h-2.5 w-2.5 shrink-0">
            {syncDot.pulse ? (
              <span className={cn("absolute inline-flex h-full w-full rounded-full opacity-60 animate-ping", syncDot.color)} />
            ) : null}
            <span className={cn("relative inline-flex h-2.5 w-2.5 rounded-full", syncDot.color)} />
          </span>
          <div className="min-w-0">
            <div data-testid="tip-sync-label" className={cn("text-[13px] uppercase tracking-[0.2em] mono", syncDot.labelColor)}>{syncDot.label}</div>
            {syncDot.detail ? <div className={cn(hintCls, "mt-1")}>{syncDot.detail}</div> : null}
          </div>
        </div>

        <Tile
          testid="tip-cell-tip"
          flash={flash}
          icon={Cube}
          label="Tip"
          value={loading ? skeleton("w-24") : <>#{tip.height ?? 0}</>}
          hint={`${tip?.tx_count ?? 0} tx · ${formatLovelace(tip?.fees, "ada")}`}
        />
        <Tile
          testid="tip-cell-epoch"
          icon={Lightning}
          label="Epoch"
          value={loading ? skeleton("w-14") : tip.epoch}
          hint={`epoch slot ${liveEpochSlot.toLocaleString("en-US")}`}
        />
        <Tile
          testid="tip-cell-slot"
          icon={StackSimple}
          label="Slot"
          value={loading ? skeleton("w-24") : liveAbsoluteSlot.toLocaleString("en-US")}
          hint="absolute · +1/s"
        />
        <Tile
          testid="tip-cell-countdown"
          icon={Hourglass}
          label="Epoch ends in"
          value={!epochEndUnix ? skeleton("w-24") : isStale ? "—" : formatCountdown(epochEndUnix)}
          hint={!epochEndUnix ? "—" : isStale ? "snapshot may be stale" : new Date(epochEndUnix * 1000).toUTCString().replace("GMT", "UTC")}
        />
        <Tile
          testid="tip-cell-pools"
          icon={CirclesThreePlus}
          label="ADA pools"
          value={stats ? formatBigCount(stats.pools_count) : skeleton("w-14")}
          hint="registered on-chain"
        />
        <Tile
          testid="tip-cell-tx-total"
          icon={ArrowsLeftRight}
          label="Total tx's"
          value={stats ? formatBigCount(stats.total_tx_count) : skeleton("w-20")}
          hint="all time"
        />

        {/* last block */}
        <div data-testid="tip-cell-last-block" className={tileCls}>
          <span className="relative inline-flex h-2.5 w-2.5 shrink-0">
            <span className="absolute inline-flex h-full w-full rounded-full opacity-60 animate-ping bg-emerald-400" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-400" />
          </span>
          <div className="min-w-0">
            <div className={labelCls}>last block</div>
            <div className="mt-0.5 font-heading text-[16px] md:text-[18px] leading-tight tabnum text-[#22d3ee] text-glow-cyan whitespace-nowrap">
              {formatRelativeTime(tip?.time)}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
